'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ApiResponse, PriceExplanationResponse } from '@/lib/types/openai';

interface PricingComparisonProps {
  serviceType: string;
  vehicleBrand: string;
}

// Response cache to avoid duplicate API calls
const responseCache = new Map<string, PriceExplanationResponse>();
// Pending requests to prevent duplicate simultaneous calls
const pendingRequests = new Map<string, Promise<PriceExplanationResponse | null>>();

const PricingComparison = ({ serviceType, vehicleBrand }: PricingComparisonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState<PriceExplanationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const mockPricing: Record<string, { dealer: string; independent: string }> = {
    'Oil Change': { dealer: '$89', independent: '$45' },
    'Brake Service': { dealer: '$450', independent: '$280' },
    'Tire Rotation': { dealer: '$65', independent: '$35' },
    'Engine Diagnostics': { dealer: '$185', independent: '$95' },
    'Air Filter Replacement': { dealer: '$75', independent: '$40' },
    'Battery Service': { dealer: '$195', independent: '$120' },
    'Body Shop': { dealer: '$1250', independent: '$680' },
  };

  const fetchExplanation = useCallback(async (
    service: string,
    brand: string,
    pricing: { dealer: string; independent: string }
  ): Promise<PriceExplanationResponse | null> => {
    const cacheKey = `${service}-${brand}`;

    // Check cache first
    if (responseCache.has(cacheKey)) {
      return responseCache.get(cacheKey)!;
    }

    // Check if request is already pending
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey)!;
    }

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const requestPromise = (async () => {
      try {
        const res = await fetch('/api/gemini/price-explanation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            serviceType: service,
            vehicleBrand: brand,
            dealerPrice: pricing.dealer,
            independentPrice: pricing.independent,
          }),
          signal: abortController.signal,
        });

        const data: ApiResponse<PriceExplanationResponse> = await res.json();

        if (data.success && data.data) {
          // Cache successful response
          responseCache.set(cacheKey, data.data);
          return data.data;
        } else {
          const errorMsg = 'error' in data ? data.error.message : 'Unknown error';
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        // Don't throw on abort - it's expected
        if (err.name === 'AbortError') {
          return null;
        }
        throw err;
      } finally {
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store pending request
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }, []);

  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!serviceType || !vehicleBrand) {
      setExplanation(null);
      setError(null);
      return;
    }

    const pricing = mockPricing[serviceType];
    if (!pricing) {
      setExplanation(null);
      return;
    }

    const cacheKey = `${serviceType}-${vehicleBrand}`;

    // Check cache immediately for instant response
    if (responseCache.has(cacheKey)) {
      setExplanation(responseCache.get(cacheKey)!);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Debounce the API call to prevent rapid-fire requests
    setIsLoading(true);
    setError(null);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const result = await fetchExplanation(serviceType, vehicleBrand, pricing);

        if (result) {
          setExplanation(result);
          setError(null);
        }
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load price explanation.';
        setError(errorMessage);
        console.error('Price explanation error:', err);
      } finally {
        setIsLoading(false);
      }
    }, 800); // 800ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [serviceType, vehicleBrand, fetchExplanation]);

  if (!serviceType || !vehicleBrand) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-8 text-center">
        <Icon name="ChartBarIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="font-body text-text-secondary">
          Select a service and vehicle brand to compare pricing
        </p>
      </div>
    );
  }

  const pricing = mockPricing[serviceType];
  if (!pricing) return null;

  const dealerValue = parseFloat(pricing.dealer.replace('$', ''));
  const independentValue = parseFloat(pricing.independent.replace('$', ''));
  const savings = dealerValue - independentValue;
  const savingsPercent = Math.round((savings / dealerValue) * 100);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl shadow-elevation-lg p-6 sm:p-8">
        <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">
          {serviceType} for {vehicleBrand}
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Dealership Card */}
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border-2 border-primary/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-text-primary">
                Dealership
              </h3>
              <Icon name="BuildingOfficeIcon" size={24} className="text-primary" />
            </div>
            <p className="font-heading text-4xl font-bold text-primary mb-4">
              {pricing.dealer}
            </p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  OEM parts guaranteed
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  Manufacturer warranty
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  Certified technicians
                </span>
              </li>
            </ul>
          </div>

          {/* Independent Mechanic Card */}
          <div className="bg-gradient-to-br from-accent/5 to-accent/10 rounded-xl border-2 border-accent/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-text-primary">
                Independent
              </h3>
              <Icon name="WrenchScrewdriverIcon" size={24} className="text-accent" />
            </div>
            <p className="font-heading text-4xl font-bold text-accent mb-4">
              {pricing.independent}
            </p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  Quality aftermarket parts
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  Experienced professionals
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-text-secondary">
                  Flexible service options
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Savings Banner */}
        <div className="mt-6 bg-gradient-to-r from-success/10 to-success/5 rounded-xl border border-success/20 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success">
                <Icon name="CurrencyDollarIcon" size={20} className="text-white" />
              </div>
              <div>
                <p className="font-heading text-sm font-semibold text-text-primary">
                  Potential Savings
                </p>
                <p className="font-body text-xs text-text-secondary">
                  By choosing an independent mechanic
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-2xl font-bold text-success">
                ${savings}
              </p>
              <p className="font-body text-xs text-text-secondary">
                ({savingsPercent}% less)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI-Powered Explanation */}
      {isLoading && (
        <div className="bg-card rounded-xl shadow-elevation-md p-6">
          <div className="flex items-center space-x-3">
            <Icon name="ArrowPathIcon" size={24} className="text-primary animate-spin" />
            <p className="font-body text-sm text-text-secondary">
              AI analyzing price differences...
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 flex-shrink-0" />
            <p className="font-body text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={async () => {
              if (pricing) {
                const cacheKey = `${serviceType}-${vehicleBrand}`;
                responseCache.delete(cacheKey);
                setIsLoading(true);
                setError(null);
                try {
                  const result = await fetchExplanation(serviceType, vehicleBrand, pricing);
                  if (result) {
                    setExplanation(result);
                    setError(null);
                  }
                } catch (err: any) {
                  setError(err instanceof Error ? err.message : 'Failed to load price explanation.');
                } finally {
                  setIsLoading(false);
                }
              }
            }}
            disabled={isLoading}
            className="text-xs font-semibold text-red-700 hover:text-red-900 underline ml-4 flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {explanation && !isLoading && (
        <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-text-primary">
              AI Cost Analysis
            </h3>
          </div>

          <p className="font-body text-text-secondary">
            {explanation.explanation}
          </p>

          <div className="space-y-3">
            <h4 className="font-heading text-sm font-semibold text-text-primary">
              Key Factors:
            </h4>
            <ul className="space-y-2">
              {explanation.factors.map((factor, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    {factor}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-accent/10 rounded-lg border border-accent/20 p-4">
            <div className="flex items-start space-x-2">
              <Icon name="LightBulbIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                  Savings Insight
                </p>
                <p className="font-body text-sm text-text-secondary">
                  {explanation.savingsInsight}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-heading text-sm font-semibold text-text-primary">
              Recommendations:
            </h4>
            <ul className="space-y-2">
              {explanation.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="ArrowRightIcon" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-text-secondary">
                    {rec}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingComparison;